import React, { useEffect } from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  CircularProgress,
  Typography,
  Alert,
  IconButton,
  Tooltip
} from '@mui/material';
import { RotateCcw } from 'lucide-react';
import { useAppDispatch, useAppSelector } from '../../store';
import { selectSpeechState } from '../../store/selectors';
import { loadDevices, setSelectedDevice } from '../../store/slices/speechSlice';

interface MicrophoneSelectorProps {
  disabled?: boolean;
  size?: 'small' | 'medium';
  variant?: 'standard' | 'outlined' | 'filled';
}

export const MicrophoneSelector: React.FC<MicrophoneSelectorProps> = ({
  disabled = false,
  size = 'small',
  variant = 'outlined'
}) => {
  const dispatch = useAppDispatch();
  const speechState = useAppSelector(selectSpeechState);

  // Load devices on component mount
  useEffect(() => {
    dispatch(loadDevices());
  }, [dispatch]);

  const handleDeviceChange = (deviceId: string) => {
    dispatch(setSelectedDevice(deviceId));
  };

  const handleRefreshDevices = () => {
    dispatch(loadDevices());
  };

  // Check if microphone access is supported
  if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
    return (
      <Alert severity="warning" sx={{ mt: 1 }}>
        Microphone access not supported in this browser
      </Alert>
    );
  }

  // Filter for audio input devices
  const audioInputDevices = speechState.availableDevices.filter(
    device => device.kind === 'audioinput'
  );

  return (
    <Box sx={{ minWidth: 200 }}>
      <FormControl fullWidth size={size} variant={variant} disabled={disabled}>
        <InputLabel id="microphone-select-label">Microphone</InputLabel>
        <Select
          labelId="microphone-select-label"
          id="microphone-select"
          value={speechState.selectedDeviceId}
          label="Microphone"
          onChange={(e) => handleDeviceChange(e.target.value)}
          endAdornment={
            <Tooltip title="Refresh microphone list">
              <IconButton
                onClick={handleRefreshDevices}
                disabled={speechState.isLoadingDevices}
                size="small"
                sx={{ mr: 1 }}
              >
                {speechState.isLoadingDevices ? (
                  <CircularProgress size={16} />
                ) : (
                  <RotateCcw size={16} />
                )}
              </IconButton>
            </Tooltip>
          }
        >
          <MenuItem value="default">
            <Typography>System Default</Typography>
          </MenuItem>
          {audioInputDevices.map((device) => (
            <MenuItem key={device.deviceId} value={device.deviceId}>
              <Typography>
                {device.label || `Microphone ${device.deviceId.slice(0, 8)}...`}
              </Typography>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      
      {speechState.isLoadingDevices && (
        <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
          <CircularProgress size={16} sx={{ mr: 1 }} />
          <Typography variant="caption" color="text.secondary">
            Loading microphones...
          </Typography>
        </Box>
      )}
      
      {speechState.error && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {speechState.error}
        </Alert>
      )}
    </Box>
  );
};
